import { useEffect, useMemo } from 'react'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'

export type SearchBurnResult = {
  enabled: boolean
  pointsToBurn?: number
  priceInPoints?: number
  hideZeroCash?: boolean
}

interface UseSearchBurnPointsOptions {
  disabled?: boolean
}

interface ResolveSearchBurnResultOptions {
  priceInPoints: number | undefined
  hasSelectedBurnToggle: boolean
  disabled?: boolean
  contextPointsToBurn?: number
  userPoints: number
}

export function resolveSearchBurnResult({
  priceInPoints,
  hasSelectedBurnToggle,
  disabled,
  contextPointsToBurn,
  userPoints,
}: ResolveSearchBurnResultOptions): SearchBurnResult {
  if (disabled || !hasSelectedBurnToggle || priceInPoints === undefined) {
    return { enabled: false }
  }

  const pointsToBurn = contextPointsToBurn !== undefined
    ? Math.min(contextPointsToBurn, priceInPoints)
    : Math.min(userPoints, priceInPoints)

  const hideZeroCash = contextPointsToBurn === undefined

  return { enabled: true, pointsToBurn, priceInPoints, hideZeroCash }
}

/**
 * Calculates how many points to burn for a given price on the search page,
 * based on the current burn toggle/slider state.
 *
 * Uses the batch burn API for backend-calculated priceInPoints.
 * Returns disabled until the backend result is available.
 */
export default function useSearchBurnPoints(
  price: number,
  loyaltyProductType: App.LoyaltyProductType | undefined,
  mx?: string,
  options: UseSearchBurnPointsOptions = {},
): SearchBurnResult {
  const { disabled = false } = options
  const dispatch = useAppDispatch()
  const { hasSelectedBurnToggle, pointsToBurn: contextPointsToBurn } = useSearchBurnEnabled()
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')

  const batchKey = useMemo(() => {
    if (disabled || !hasSelectedBurnToggle || !loyaltyProductType) return undefined
    return buildBurnKey({ price, loyaltyProductType, mx }, customerId)
  }, [disabled, hasSelectedBurnToggle, loyaltyProductType, price, mx, customerId])

  const batchResult = useAppSelector(state => {
    if (!batchKey) return undefined
    return state.loyalty.pointsBurnBatchCalculations?.[batchKey]?.data
  })

  useEffect(() => {
    if (disabled || !hasSelectedBurnToggle || !loyaltyProductType) return
    dispatch(submitLoyaltyPointsBurnCalculationRequest({ price, loyaltyProductType, mx }))
  }, [disabled, dispatch, hasSelectedBurnToggle, price, loyaltyProductType, mx, customerId])

  return useMemo(() => resolveSearchBurnResult({
    priceInPoints: batchResult?.priceInPoints,
    hasSelectedBurnToggle,
    disabled,
    contextPointsToBurn,
    userPoints,
  }), [disabled, hasSelectedBurnToggle, contextPointsToBurn, userPoints, batchResult])
}
